import { ProfileCardComponent } from '@/components/public-profile-card/profileCard'
import { createProfileDetailsForTS, getFullNameCSS } from '@/lib/profileFunction'
import Link from 'next/link'
import { TypesenseData } from '@/lib/typesenseFunction'

interface IProps {
  profileData: TypesenseData
  showSeeProfileBtn?: boolean
  isHomepage?: boolean
}

const typeStyles: Record<
  string,
  {
    container: string
    pictureDiv: string
    fullName: string
    details: string
    descriptionWrapper: string
    information: string
  }
> = {
  home: {
    container: 'flex-col w-[220px] h-[445px] pt-[15px] px-[15px] pb-[24px]',
    pictureDiv: 'w-[190px] h-[188px] flex rounded-md',
    fullName: 'bottom-[0px]',
    details: 'h-[84px] text-sm mb-2',
    descriptionWrapper: 'flex',
    information: 'min-h-[225px]',
  },
  default: {
    container:
      'flex-row h-[120px] sm:flex-col sm:w-[220px] sm:h-[445px] sm:pt-[15px] sm:px-[15px] sm:pb-[24px] hover:border-gray-600 sm:ml-2.5 mb-3 sm:mb-2.5',
    pictureDiv: 'w-[100px] h-[100px] sm:w-[190px] sm:h-[188px] flex rounded-md mx-[12px]',
    fullName: 'bottom-[3px] sm:bottom-[0px]',
    details: 'h-[70px] sm:h-[84px] relative bottom-[8px] sm:bottom-[0px] text-sm mb-2',
    descriptionWrapper: 'hidden sm:flex',
    information: 'w-[190px] sm:min-h-[225px]',
  },
}

// Shared across all types
const shared = {
  containerBase:
    'flex justify-center items-center bg-gray-50 rounded-md border border-gray-200 shadow-cards hover:shadow-cards hover:bg-gray-100',
  pictureCSS: 'rounded-md',
  fullNameDiv: 'sm:pt-2',
  descriptionText: (showBtn?: boolean, isHomepage?: boolean) =>
    `${showBtn ? 'profileDescriptionCenter' : 'profileDescription'} ${
      showBtn ? '!line-clamp-3 !max-h-[60px]' : 'line-clamp-5 max-h-[100px]'
    } text-sm ${isHomepage ? 'block' : 'hidden sm:block'}`,
}

export const LobbyProfileCard = ({ profileData, isHomepage, showSeeProfileBtn }: IProps) => {
  const typeKey = isHomepage ? 'home' : 'default'
  const styles = typeStyles[typeKey]

  const arrayOfData = createProfileDetailsForTS(profileData)
  const fullNameLength = profileData.fullName?.length ?? 0
  const isProfessor = profileData.status?.includes('Professor')
  const href = isProfessor ? `/professor/${profileData.slug}` : `/individual/${profileData.slug}`

  let nodeDescription = <></>
  if (profileData?.description) {
      nodeDescription = (
        <div className={styles.descriptionWrapper}>
          <span className={shared.descriptionText(showSeeProfileBtn, isHomepage)}>
            {profileData.description}
          </span>
        </div>
      )
  }

  return (
    <Link
      className={`${shared.containerBase} ${styles.container} ${isHomepage ? 'cursor-auto' : ''}`}
      href={href}
      onClick={(e) => isHomepage && e.preventDefault()}
      prefetch={false}
    >
      <ProfileCardComponent
        profileData={profileData}
        profilePicture={{
          pictureDivCSS: styles.pictureDiv,
          pictureCSS: shared.pictureCSS,
          sizes: '(max-width: 640px) 100px, 190px',
          isPriority: profileData.__position === 1,
        }}
        profileFullName={{
          fullNameDivCSS: shared.fullNameDiv,
          fullNameCSS: `${getFullNameCSS(fullNameLength)} block font-bold relative ${
            styles.fullName
          }`,
        }}
        moreDetails={{
          ifShow: true,
          value: arrayOfData,
          showMore: false,
          detailsCSS: styles.details,
        }}
        profileDescription={{
          ifShow: true,
          descriptionNode: nodeDescription,
        }}
        informationCSS={styles.information}
        seeProfileBtn={{ ifShow: showSeeProfileBtn , btnCSS: 'w-full !px-10 justify-center' }}
      />
    </Link>
  )
}

export default LobbyProfileCard
